import {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useStoreActions, useStoreState} from 'store';
import {
    extractQueryWordAtClick,
    getQueryWords,
} from '@/views/search/utils/extractQueryWordAtClick';

type SimilarWordsScope = 'search' | 'header';

interface Options {
    queryValue: string;
    onAppendWord: (word: string) => void;
    scope?: SimilarWordsScope;
}

export const useQuerySimilarWordsPopover = ({
    queryValue,
    onAppendWord,
    scope = 'search',
}: Options) => {
    const [popoverTarget, setPopoverTarget] = useState<HTMLElement | null>(null);
    const [showPopover, setShowPopover] = useState(false);

    const activeWord = useStoreState((state) => (
        scope === 'header' ? state.search.headerActiveWord : state.search.activeWord
    ));
    const similarWords = useStoreState((state) => (
        scope === 'header' ? state.search.headerSimilarWords : state.search.similarWords
    ));
    const loadingSimilarWords = useStoreState((state) => (
        scope === 'header' ? state.search.loadingHeaderSimilarWords : state.search.loadingSimilarWords
    ));

    const setActiveWord = useStoreActions((actions) => actions.search.setActiveWord);
    const setSimilarWords = useStoreActions((actions) => actions.search.setSimilarWords);
    const setLoadingSimilarWords = useStoreActions((actions) => actions.search.setLoadingSimilarWords);
    const setHeaderActiveWord = useStoreActions((actions) => actions.search.setHeaderActiveWord);
    const setHeaderSimilarWords = useStoreActions((actions) => actions.search.setHeaderSimilarWords);
    const setLoadingHeaderSimilarWords = useStoreActions((actions) => actions.search.setLoadingHeaderSimilarWords);

    const existingWords = useMemo(() => getQueryWords(queryValue), [queryValue]);

    const clearOtherScope = useCallback(() => {
        if (scope === 'header') {
            setActiveWord(null);
            setSimilarWords([]);
            setLoadingSimilarWords(false);
            return;
        }

        setHeaderActiveWord(null);
        setHeaderSimilarWords([]);
        setLoadingHeaderSimilarWords(false);
    }, [
        scope,
        setActiveWord,
        setHeaderActiveWord,
        setHeaderSimilarWords,
        setLoadingHeaderSimilarWords,
        setLoadingSimilarWords,
        setSimilarWords,
    ]);

    const clearActiveWord = useCallback(() => {
        if (scope === 'header') {
            setHeaderActiveWord(null);
            setHeaderSimilarWords([]);
            setLoadingHeaderSimilarWords(false);
            return;
        }

        setActiveWord(null);
        setSimilarWords([]);
        setLoadingSimilarWords(false);
    }, [
        scope,
        setActiveWord,
        setHeaderActiveWord,
        setHeaderSimilarWords,
        setLoadingHeaderSimilarWords,
        setLoadingSimilarWords,
        setSimilarWords,
    ]);

    const setScopeActiveWord = useCallback((word: string | null) => {
        if (scope === 'header') {
            setHeaderActiveWord(word);
            return;
        }

        setActiveWord(word);
    }, [scope, setActiveWord, setHeaderActiveWord]);

    const fetchSimilarWords = useCallback((word: string) => {
        const setLoading = scope === 'header' ? setLoadingHeaderSimilarWords : setLoadingSimilarWords;
        const setWords = scope === 'header' ? setHeaderSimilarWords : setSimilarWords;

        const fetchData = async () => {
            setLoading(true);

            try {
                const response = await axios.get(window.URLS.word2VecSimilarWords, {
                    headers: {'Content-Type': 'application/json'},
                    params: {
                        query: decodeURIComponent(word),
                    },
                });
                setWords(response.data);
            } catch {
                setWords([]);
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [
        scope,
        setHeaderSimilarWords,
        setLoadingHeaderSimilarWords,
        setLoadingSimilarWords,
        setSimilarWords,
    ]);

    const handleInputClick = useCallback((event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const inputElement = event.currentTarget;
        const word = extractQueryWordAtClick(inputElement);

        if (!word) {
            setShowPopover(false);
            return;
        }

        if (activeWord !== word) {
            clearOtherScope();
            setScopeActiveWord(word);
            fetchSimilarWords(word);
        }

        setTimeout(() => setShowPopover(true), 100);
    }, [activeWord, clearOtherScope, fetchSimilarWords, setScopeActiveWord]);

    const handlePopoverHide = useCallback(() => {
        setShowPopover(false);
    }, []);

    const handleAddWord = useCallback((word: string) => {
        onAppendWord(word);
    }, [onAppendWord]);

    const handleQueryInputChange = useCallback(() => {
        clearActiveWord();
        setShowPopover(false);
    }, [clearActiveWord]);

    useEffect(() => {
        if (!activeWord) {
            setShowPopover(false);
        }
    }, [activeWord]);

    return {
        setInputElement: setPopoverTarget,
        popoverTarget,
        activeWord,
        similarWords,
        loadingSimilarWords,
        showPopover,
        existingWords,
        handleInputClick,
        handlePopoverHide,
        handleAddWord,
        handleQueryInputChange,
    };
};
